<template>
  <div id="clientList">
    <div class="atb-flex atb-flex-row atb-gap-4 atb-pt-6 atb-px-4 atb-pb-4">
      <div class="atb-flex-grow">
        <div class="eds-table-wrap atb-w-full atb-h-full">
          <Toolbar
            class-name="atb-bg-white atb-border-t-0 atb-border-x-0 atb-border-b atb-border-solid atb-border-gray-300"
          >
            <template #toolbarSection>
              <ToolbarSection>
                <template #sectionContent>
                  <Button
                    id="clientListRefreshButton"
                    motif="tertiary"
                    :title="$t('refresh')"
                    aria-label="refresh button"
                    @click="refreshClients"
                  >
                    <template #icon>
                      <span class="material-icons">loop</span>
                    </template>
                  </Button>
                </template>
              </ToolbarSection>
              <ToolbarSection>
                <template #sectionContent>
                  <Button
                    id="clientsListCreateButton"
                    motif="tertiary"
                    :title="$t('create')"
                    aria-label="create button"
                    @click="openCreateForm"
                  >
                    <template #icon>
                      <span class="material-icons">add_circle_outline</span>
                    </template>
                  </Button>
                </template>
              </ToolbarSection>
              <ToolbarSection>
                <template #sectionContent>
                  <Button
                    id="clientsListDeleteButton"
                    motif="tertiary"
                    :title="$t('delete')"
                    aria-label="delete button"
                    @click="deleteClient"
                  >
                    <template #icon>
                      <span class="material-icons icon">
                        delete_outline_icon
                      </span>
                    </template>
                  </Button>
                </template>
              </ToolbarSection>
            </template>
          </Toolbar>
          <AgGridVue
            ref="clientListRef"
            id="clientsListAllClientsGrid"
            :style="{ height: '500px' }"
            class="ag-theme-eds atb-w-full atb-h-full"
            :row-model-type="'infinite'"
            :column-defs="columnDefs"
            :pagination="true"
            :pagination-page-size="rowsPerPage"
            :suppress-pagination-panel="true"
            :cache-block-size="rowsPerPage"
            :row-selection="'single'"
            @grid-ready="onGridReady"
            :row-multi-select-with-click="true"
            :suppress-cell-selection="true"
            :suppress-row-click-selection="true"
          />
          <Pagination
            v-model="currentPage"
            :page-count="totalPages"
            :total-rows="totalRows"
            :page-size="rowsPerPage"
            @pageClick="changePageAndRefresh"
            @pageSizeChange="changePageSizeAndRefresh"
            class="atb-bg-white"
          />
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
  import ClickableCell from '@/app/shared/components/grid-cells/ClickableCell.vue';
  import Button from '@atb-ui/eds-vue/src/components/Button.vue';
  import Toolbar from '@atb-ui/eds-vue/src/components/Toolbar/Toolbar.vue';
  import ToolbarSection from '@atb-ui/eds-vue/src/components/Toolbar/ToolbarSection.vue';
  import Pagination from '@atb-ui/eds-vue/src/components/Pagination.vue';
  import { AgGridVue } from 'ag-grid-vue3/lib/AgGridVue';
  import { ListEvents } from '@/app/shared/types/list-events.interface';
  import { Client } from '@/app/shared/types/models/client.interface';
  import { useI18n } from 'vue-i18n';
  import { useClientStore } from '../client-store';
  import { storeToRefs } from 'pinia';
  import { Ref, ref } from 'vue';
  import { GridApi, GridReadyEvent, IGetRowsParams } from 'ag-grid-community';
  import { usePagination } from '@/app/shared/hooks/use-pagination';

  interface ClientListEvents extends ListEvents<Client> {}
  const emit = defineEmits<ClientListEvents>();
  const { t } = useI18n();
  const clientStore = useClientStore();
  const { clientList } = storeToRefs(clientStore);
  const gridApi = ref(null) as Ref<GridApi | null>;

  const {
    currentPage,
    rowsPerPage,
    totalRows,
    totalPages,
    changePage,
    changePageSize,
    calculatePagination,
  } = usePagination(
    clientList.value.numberOfElements,
    clientList.value.totalElements,
    gridApi
  );

  rowsPerPage.value = 50;

  const changePageAndRefresh = (page: number) => {
    changePage(page);
    gridApi.value?.setDatasource(getAllClients());
  };

  const changePageSizeAndRefresh = (newPageSize: number | string) => {
    changePageSize(newPageSize);
    gridApi.value?.setDatasource(getAllClients());
  };

  const editClient = async (clientId: number, clientName: string) => {
    emit('edit', {
      id: clientId,
      name: clientName,
      isNew: false,
    });
  };

  const columnDefs = [
    {
      field: 'check',
      headerName: '',
      width: 56,
      suppressSizeToFit: true,
      headerCheckboxSelection: true,
      checkboxSelection: true,
    },
    {
      field: 'clientName',
      headerName: t('Client Name'),
      suppressMovable: true,
      width: 200,
      minWidth: 200,
      sortable: true,
      cellRendererFramework: ClickableCell,
      cellRendererParams: {
        onClick: editClient,
      },
    },
    {
      field: 'description',
      headerName: t('Description'),
      suppressMovable: true,
      flex: 1,
      sortable: true,
    },
  ];

  const getAllClients = () => {
    return {
      getRows: async ({ successCallback, failCallback }: IGetRowsParams) => {
        try {
          gridApi.value?.showLoadingOverlay();

          const pageNumber = currentPage.value > 0 ? currentPage.value - 1 : 0;

          await clientStore.fetchClients(
            pageNumber,
            rowsPerPage.value,
            'id',
            'asc'
          );

          const clients = clientList.value.content || [];
          const totalElements = clientList.value.totalElements || 0;

          if (clients.length > 0) {
            gridApi.value?.hideOverlay();
          } else {
            gridApi.value?.showNoRowsOverlay();
          }

          calculatePagination(totalElements);

          successCallback(clients, clientList.value.numberOfElements || 0);
        } catch (error) {
          gridApi.value?.showNoRowsOverlay();
          failCallback();
          console.error((error as Error).message);
        }
      },
    };
  };

  const onGridReady = (params: GridReadyEvent) => {
    gridApi.value = params.api;
    gridApi.value?.setDatasource(getAllClients());
    gridApi.value.sizeColumnsToFit();
  };

  const refreshClients = (): void => {
    gridApi.value?.purgeInfiniteCache();
  };

  const deleteClient = () => {
    const selectedRows: Client[] | undefined = gridApi.value?.getSelectedRows();
    emit('deleteById', selectedRows ? selectedRows[0].id : '');
  };

  const openCreateForm = () => {
    emit('create');
  };

  defineExpose({
    refreshClients,
    gridApi,
  });
</script>

<style scoped>
  .icon {
    width: 20px;
  }
</style>
